import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { ActivityMenuStatus } from '~/analytics/bootcamp/bootcampEvents.types'
import { BootcampPageUrlParamsType } from '~/bootcamp/types/bootcamp.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectVccPageTitle } from '~/bootcamp/slices/vcc-data.slice'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ChevronRightIcon from '~/components/Icons/svgs/ChevronRightIcon'
import CircleIconButton from '~/bootcamp/components/CircleIconButton'
import { selectNavigateToLearningPath, setNavigateFromLearningPath } from '~/redux/slices/dashboard'
import { useGetBackFromBootcampUrl } from '~/bootcamp/hooks/useBackToCurriculumPath'

import ActivityNavigation from './ActivityNavigation'

const VideoCourseHeader = (): React.ReactElement => {
  const { t } = useTranslation('generics')
  const { query } = useRouter()
  const { subtopic, action, key, level, moduleKey } = query as unknown as BootcampPageUrlParamsType
  const dispatch = useAppDispatch()
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true)
  const title = useAppSelector(selectVccPageTitle)
  const navigateToLearningPathFlag = useAppSelector(selectNavigateToLearningPath)
  const [isNavigateToLearningPath, setNavigateToLearningPath] = useState(false)

  useEffect(() => {
    if (navigateToLearningPathFlag) {
      setNavigateToLearningPath(true)
    }
    return () => {
      dispatch(setNavigateFromLearningPath(false))
    }
  }, [dispatch, navigateToLearningPathFlag])

  const trackMenuClick = (isExpanded: boolean): void => {
    bootcampAnalytics.logClickActivityMenu({
      category: subtopic,
      subject: key,
      bootcamp: moduleKey,
      level,
      bootcamp_page_type: action,
      status: isExpanded ? ActivityMenuStatus.OPENED : ActivityMenuStatus.CLOSED,
    })
  }

  const toggleDropdown = (): void => {
    trackMenuClick(!isMenuCollapsed)
    setIsMenuCollapsed(!isMenuCollapsed)
  }

  return (
    <>
      <header className="mx-auto relative flex flex-col md:flex-row justify-start md:justify-between items-start md:items-center p-8">
        <BackToBootcampButton isNavigateToLearningPath={isNavigateToLearningPath} />

        <div className="flex items-center w-full md:w-auto">
          <h1 className="text-lg md:text-h3 text-white font-bold drop-shadow-lg mr-4 flex-1 md:flex-auto">{title}</h1>

          <button
            aria-label={t('ariaLabelButtons.expandDropdownMenu')}
            className="cursor-pointer"
            onClick={toggleDropdown}
          >
            <span
              className={`transform transition-transform duration-300 inline-block mt-1 ${
                !isMenuCollapsed ? 'rotate-90' : 'rotate-0'
              }`}
            >
              <SVGIcon icon={ChevronRightIcon} pathClassName="text-white" />
            </span>
          </button>
        </div>

        <span />

        <div
          className={`absolute top-28 left-0 right-0 md:w-full md:left-1/2 z-40 px-4 md:max-w-screen-sm md:-translate-x-1/2 origin-top-right transition duration-300 ease-in-out transform mt-1 ${
            !isMenuCollapsed ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'
          }`}
        >
          <div className="w-full shadow-large rounded">
            <ActivityNavigation isMenuCollapsed={isMenuCollapsed} onMenuClick={toggleDropdown} />
          </div>
        </div>
      </header>
    </>
  )
}

const BackToBootcampButton = ({
  isNavigateToLearningPath = false,
}: {
  isNavigateToLearningPath: boolean
}): React.ReactElement => {
  const { t } = useTranslation('generics')

  const bootcampListLink = useGetBackFromBootcampUrl({ isNavigateToLearningPath })

  return (
    <CircleIconButton
      aria-label={t('ariaLabelButtons.backToBootcamp')}
      className="w-9 h-9 bg-white mb-2 md:mb-0"
      iconColor="text-purple"
      iconName="arrow-left-bold"
      link={bootcampListLink}
    />
  )
}

export default VideoCourseHeader
